/**
 * Enhanced Notification Bell Component
 * 
 * Uses Hybrid Notification System:
 * - Pusher for realtime updates (no polling)
 * - Web Push for browser notifications
 * - Integrated with NotificationContext
 */

import { useState } from 'react';
import { Bell, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotifications } from '@/contexts/NotificationContext';
import { useTranslation } from 'react-i18next';

export const NotificationBell = () => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const {
        notifications,
        unreadCount,
        isLoading,
        markAsRead,
        deleteNotification,
    } = useNotifications();

    const handleMarkAsRead = async (notificationId: number) => {
        await markAsRead(notificationId);
    };

    const handleDelete = async (notificationId: number) => {
        await deleteNotification(notificationId);
    };

    const formatTimeAgo = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (seconds < 60) return t('notifications.time.just_now');
        if (seconds < 3600) return t('notifications.time.minutes_ago', { count: Math.floor(seconds / 60) });
        if (seconds < 86400) return t('notifications.time.hours_ago', { count: Math.floor(seconds / 3600) });
        return t('notifications.time.days_ago', { count: Math.floor(seconds / 86400) });
    };

    const renderSmartNotification = (notification: any) => {
        let title = notification.title;
        let message = notification.message;

        // 1. Detect New User Registration
        if (title.includes("New User Registration") || message.includes("A new user has registered")) {
            const nameMatch = message.match(/👤 Name: (.*)/);
            const emailMatch = message.match(/📧 Email: (.*)/);
            const roleMatch = message.match(/🏷️ Role: (.*)/);
            const typeMatch = message.match(/📝 Type: (.*)/);

            if (nameMatch) {
                title = t('notifications.templates.new_user_registration_title', { defaultValue: title });
                message = t('notifications.templates.new_user_registration', {
                    userName: nameMatch[1],
                    userEmail: emailMatch ? emailMatch[1] : '',
                    userRole: roleMatch ? roleMatch[1] : '',
                    registrationType: typeMatch ? typeMatch[1] : '',
                    defaultValue: message
                });
                return { title, message };
            }
        }

        // 2. Detect New Subscription
        if (title.includes("New Subscription") || message.includes("A new subscription was purchased")) {
            const nameMatch = message.match(/👤 Name: (.*)/);
            const emailMatch = message.match(/📧 Email: (.*)/);
            const planMatch = message.match(/📦 Plan: (.*)/);
            const amountMatch = message.match(/💵 Amount: (.*) ([\d.]+)/);

            if (nameMatch) {
                title = t('notifications.templates.new_subscription_title', { defaultValue: title });
                message = t('notifications.templates.new_subscription', {
                    userName: nameMatch[1],
                    userEmail: emailMatch ? emailMatch[1] : '',
                    planName: planMatch ? planMatch[1] : '',
                    currency: amountMatch ? amountMatch[1] : '',
                    amount: amountMatch ? amountMatch[2] : '',
                    defaultValue: message
                });
                return { title, message };
            }
        }

        // 3. Fallback: Check if it's one of our typed templates (if any)
        if (notification.notification_type) {
            title = t(`notifications.templates.${notification.notification_type}_title`, { defaultValue: title });
            message = t(`notifications.templates.${notification.notification_type}`, {
                ...notification.metadata,
                defaultValue: message
            });
        }

        return { title, message };
    };

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-muted-foreground relative shrink-0" aria-label={t('notifications.title')}>
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                        <span className="absolute top-1 right-1 -translate-y-1 translate-x-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold leading-none flex items-center justify-center ring-2 ring-background shadow-xs pointer-events-none z-10">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-[420px] p-0 shadow-premium border border-black/5 dark:border-white/5 backdrop-blur-xl bg-background/95 rounded-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b bg-muted/30">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                        {t('notifications.title')}
                        {unreadCount > 0 && (
                            <span className="bg-blue-500 text-white text-[10px] px-2 py-0.5 rounded-full">
                                {t('notifications.new_count', { count: unreadCount })}
                            </span>
                        )}
                    </h3>
                </div>

                {/* Notifications list */}
                {notifications.length > 0 ? (
                    <ScrollArea className="h-[500px]">
                        <div className="divide-y divide-border">
                            {notifications.map((notification) => {
                                const content = renderSmartNotification(notification);
                                return (
                                    <div
                                        key={notification.id}
                                        className={`p-4 hover:bg-muted/50 transition-colors relative group ${!notification.deleted_at
                                            ? 'bg-blue-50/50 dark:bg-blue-950/10'
                                            : ''
                                            }`}
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h4 className={`font-semibold text-sm ${!notification.deleted_at ? 'text-foreground' : 'text-muted-foreground'}`}>
                                                        {content.title}
                                                    </h4>
                                                    {!notification.deleted_at && (
                                                        <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />
                                                    )}
                                                </div>
                                                <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed mb-2">
                                                    {content.message}
                                                </p>
                                                <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                                                    {formatTimeAgo(notification.created_at)}
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                                {!notification.deleted_at && (
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-8 w-8 border-blue-100 hover:border-blue-200 hover:bg-blue-50 dark:border-blue-900/50 dark:hover:bg-blue-950/30 text-blue-600"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleMarkAsRead(notification.id);
                                                        }}
                                                        title={t('notifications.mark_as_read')}
                                                    >
                                                        <Check className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    className="h-8 w-8 border-red-100 hover:border-red-200 hover:bg-red-50 dark:border-red-900/50 dark:hover:bg-red-950/30 text-red-500"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleDelete(notification.id);
                                                    }}
                                                    title={t('notifications.delete')}
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </ScrollArea>
                ) : (
                    <div className="p-8 text-center text-muted-foreground">
                        <Bell className="h-12 w-12 mx-auto mb-2 opacity-20" />
                        <p className="text-sm">{t('notifications.no_notifications')}</p>
                    </div>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
